import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, api, ApiError, Button, Card, Input, useAuth, useFetch } from '@greenrinse/ui';

export interface Branding { name: string; brandColor: string | null; logoUrl: string | null; defaultLanguage: string }

/** Mix the brand colour into the design tokens. Light containers are derived by blending towards white. */
function applyBrandColor(hex: string | null) {
  const root = document.documentElement;
  if (!hex) { root.style.removeProperty('--gr-primary'); root.style.removeProperty('--gr-primary-container'); return; }
  root.style.setProperty('--gr-primary', hex);
  root.style.setProperty('--gr-primary-container', `color-mix(in srgb, ${hex} 16%, white)`);
}

/**
 * Applies the company's branding after sign-in (document title + primary colour + logo swap); without any branding
 * the GreenRinse defaults stay. Render once inside the authenticated layout; it draws nothing.
 */
export function BrandingLoader() {
  const { user } = useAuth();
  const branding = useFetch<Branding>(user ? '/api/company/branding' : null);
  useEffect(() => {
    const b = branding.data;
    if (!b) return;
    document.title = b.name;
    applyBrandColor(b.brandColor);
    return () => applyBrandColor(null);
  }, [branding.data]);
  const logo = branding.data?.logoUrl;
  useEffect(() => {
    // Swap the wordmark for the company logo where the shell renders one.
    if (!logo) return;
    const style = document.createElement('style');
    style.textContent = `.gr-sidenav .gr-logo, .gr-topbar .gr-logo { display: none !important; }`;
    document.head.appendChild(style);
    const imgs: HTMLElement[] = [];
    document.querySelectorAll('.gr-sidenav .gr-logo, .gr-topbar .gr-logo').forEach((el) => {
      const img = document.createElement('img');
      img.src = logo;
      img.alt = branding.data?.name ?? '';
      img.style.maxHeight = '32px'; img.style.maxWidth = '160px'; img.style.objectFit = 'contain';
      el.parentElement?.insertBefore(img, el);
      imgs.push(img);
    });
    return () => { style.remove(); imgs.forEach((i) => i.remove()); };
  }, [logo, branding.data?.name]);
  return null;
}

/** Settings → Branding: company name, brand colour and logo (Super Admin). */
export function BrandingSection() {
  const { t } = useTranslation();
  const branding = useFetch<Branding>('/api/company/branding');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#2F6B4F');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  useEffect(() => { if (branding.data) { setName(branding.data.name); setColor(branding.data.brandColor ?? '#2F6B4F'); } }, [branding.data]);

  const save = async (extra: Partial<{ logoFileId: string; removeLogo: boolean }> = {}) => {
    setBusy(true); setMsg(null);
    try {
      await api('/api/company/branding', { method: 'PUT', body: { name, brandColor: color, ...extra } });
      branding.reload(); setMsg({ tone: 'success', text: t('settings.saved') });
      window.setTimeout(() => window.location.reload(), 600); // re-apply colours/logo everywhere
    } catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); }
    finally { setBusy(false); }
  };
  const uploadLogo = async (file: File) => {
    setBusy(true); setMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const stored = await api<{ id: string }>('/api/files?category=Other', { method: 'POST', form });
      await save({ logoFileId: stored.id });
    } catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : t('common.error_generic') }); setBusy(false); }
  };

  return (
    <Card>
      <h2>🎨 {t('settings.branding_title')}</h2>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Input label={t('settings.brand_name')} value={name} onChange={(e) => setName(e.target.value)} />
      <label className="gr-field"><span style={{ fontWeight: 600 }}>{t('settings.brand_color')}</span>
        <div className="gr-row">
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 48, height: 40, border: '1px solid var(--gr-border)', borderRadius: 8, background: 'transparent' }} />
          <Input value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 120 }} />
        </div>
      </label>
      <label className="gr-field"><span style={{ fontWeight: 600 }}>{t('settings.brand_logo')}</span>
        <div className="gr-row">
          {branding.data?.logoUrl && <img src={branding.data.logoUrl} alt="logo" style={{ maxHeight: 40, maxWidth: 180, objectFit: 'contain', background: '#fff', borderRadius: 8, padding: 4, border: '1px solid var(--gr-border)' }} />}
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadLogo(f); }} />
          {branding.data?.logoUrl && <Button variant="ghost" onClick={() => void save({ removeLogo: true })}>{t('common.delete')}</Button>}
        </div>
        <span className="gr-hint">{t('settings.brand_logo_hint')}</span>
      </label>
      <Button loading={busy} onClick={() => void save()}>{t('common.save')}</Button>
    </Card>
  );
}
